import React, { useState, useEffect, useRef, useMemo } from 'react';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import { useSwipeable } from 'react-swipeable';
import { isGameFinished, BOOKMAKER_ALIASES } from '../utils/constants';
import { isBinaryMarket as isBinaryMarketUtil, getEdgeForPlayerBook } from '../utils/oddsHelpers';
import GameHeader from './GameHeader';
import H2HGameModal from './H2HGameModal';

// Create reverse alias map (caesars -> williamhill_us)
const REVERSE_ALIASES = Object.entries(BOOKMAKER_ALIASES).reduce((acc, [key, value]) => {
  acc[value] = key;
  return acc;
}, {});

const H2HDataTable = ({
  data,
  gameTime,
  gameStatus,
  gameDate,
  lastUpdate,
  game,
  formatPlayerName,
  getTeamLogoUrl,
  getBookLogoUrl,
  isMobile,
  selectedMarket,
  selectedMarketName,
  bookmakers,
  isLoading,
  availableMarkets,
  onMarketSelection,
  showTiers,
  onTierToggle,
  masterTierFilter,
  onPlayerClick,
  onOddsClick,
  onGameClick,
  clickedCellId,
  isCollapsed = false,
  onCollapseToggle,
  viewMode = 'ou'
}) => {
  // Safety check - provide defaults if game data is not fully loaded
  const safeGame = { away: 'TBD', home: 'TBD', gameIDmlb: 'loading', sport: 'nba', ...game };
  const sport = safeGame.sport?.toLowerCase() || 'nba';

  // Game modal state
  const [showGameModal, setShowGameModal] = useState(false);

  // Helper function to create cell ID for animation
  const createCellId = (gameId, playerId, market, bookmaker, line, side) => {
    return `${gameId}_${playerId}_${market}_${bookmaker}_${line}_${side}`;
  };

  // Helper function to check if cell should be animated
  const getCellClassName = (gameId, playerId, market, bookmaker, line, side) => {
    const cellId = createCellId(gameId, playerId, market, bookmaker, line, side);
    return cellId === clickedCellId ? 'bet-add-animation' : '';
  };

  // Track rendering state - prevent heavy processing until spinner paints
  const [isRendering, setIsRendering] = useState(false);
  const prevMarketRef = useRef(selectedMarket);

  // Detect market change DURING render (not in useEffect)
  if (prevMarketRef.current !== selectedMarket) {
    if (!isRendering) {
      setIsRendering(true);
    }
    prevMarketRef.current = selectedMarket;
  }

  // Clear rendering state after spinner has painted
  useEffect(() => {
    if (isRendering) {
      const timeoutId = setTimeout(() => {
        setIsRendering(false);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isRendering]);

  // Combined loading state
  const showLoading = isLoading || isRendering;

  // Block rendering heavy data while loading
  const displayData = showLoading ? [] : data;

  // Mobile carousel state
  const [mobileBookIndex, setMobileBookIndex] = useState(0);
  const booksPerPage = 5;

  // Use provided bookmakers or fall back to default list
  const allBookmakers = bookmakers && bookmakers.length > 0 ? bookmakers : ['fanduel', 'draftkings', 'betmgm', 'fanatics'];
  const totalPages = Math.ceil(allBookmakers.length / booksPerPage);

  // Get visible books based on platform
  const bookLogos = isMobile
    ? allBookmakers.slice(mobileBookIndex * booksPerPage, (mobileBookIndex + 1) * booksPerPage)
    : allBookmakers;

  // Navigation functions for mobile
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToPrevBooks = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setMobileBookIndex(prev => (prev === 0 ? totalPages - 1 : prev - 1));
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const goToNextBooks = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setMobileBookIndex(prev => (prev === totalPages - 1 ? 0 : prev + 1));
    setTimeout(() => setIsTransitioning(false), 300);
  };

  // Swipe handlers for mobile
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (isMobile && totalPages > 1) {
        goToNextBooks();
      }
    },
    onSwipedRight: () => {
      if (isMobile && totalPages > 1) {
        goToPrevBooks();
      }
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: false
  });

  const oddsCellStyle = {
    width: '80px',
    minWidth: '80px',
    maxWidth: '80px',
    textAlign: 'center',
    fontSize: '0.95rem',
    letterSpacing: '-0.025em',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  // Use shared utility for consistency with Best Bets
  const isBinaryMarket = isBinaryMarketUtil;

  // Helper to format odds with +/- sign
  const formatOdds = (odds) => {
    if (odds === null || odds === undefined || odds === '') return '-';
    const numOdds = parseInt(odds, 10);
    if (isNaN(numOdds)) return '-';
    return numOdds > 0 ? `+${numOdds}` : `${numOdds}`;
  };

  // Get best line edge info for a player
  const getBestLineEdge = (player) => {
    return getEdgeForPlayerBook(player, selectedMarket, null, sport);
  };

  // Get best line for a specific book
  const getBestLine = (player, book) => {
    return getEdgeForPlayerBook(player, selectedMarket, book, sport);
  };

  // Cell style for best odds highlighting
  // Only highlight if exactly 1 book has the best price (booksWithBestPrice === 1)
  const getCellStyle = (price, bestBookPrice, bestBookDiff, booksWithBestPrice) => {
    const style = {};

    // Only highlight if this is the best price AND only 1 book has it
    if (price === bestBookPrice && booksWithBestPrice === 1) {
      style.fontWeight = 'bold';

      if (bestBookDiff !== 1) {
        const diffValue = Math.abs(bestBookDiff);
        if (diffValue >= 0.25) {
          Object.assign(style, { backgroundColor: 'green', color: 'yellow' });
        } else if (diffValue >= 0.15) {
          Object.assign(style, { backgroundColor: '#F59E0B', color: '#1E40AF' });
        } else if (diffValue >= 0.05) {
          Object.assign(style, { backgroundColor: '#3B82F6', color: 'white' });
        } else {
          Object.assign(style, { color: '#F59E0B' });
        }
      }
    }

    return style;
  };

  // Get period text based on sport
  const getPeriodText = (period, sport) => {
    if (!period) return '';

    switch (sport) {
      case 'ncaab':
      case 'wnba':
        // College basketball and WNBA use halves
        if (period === 1) return '1st Half';
        if (period === 2) return '2nd Half';
        if (period > 2) return `OT${period - 2}`;
        break;
      case 'nba':
        // NBA uses quarters
        if (period === 1) return '1st';
        if (period === 2) return '2nd';
        if (period === 3) return '3rd';
        if (period === 4) return '4th';
        if (period > 4) return `OT${period - 4}`;
        break;
      case 'nfl':
      case 'ncaaf':
        // Football uses quarters
        if (period === 1) return '1st';
        if (period === 2) return '2nd';
        if (period === 3) return '3rd';
        if (period === 4) return '4th';
        if (period > 4) return `OT`;
        break;
      default:
        return `P${period}`;
    }
    return '';
  };

  // Format live status - works for all sports
  const formatLiveStatus = (status, game, safeGame) => {
    // Check for canceled status
    if (status === 'STATUS_CANCELED' || status?.toUpperCase()?.includes('CANCELED')) {
      return 'Canceled';
    }

    // Check for postponed status
    if (status === 'STATUS_POSTPONED' || status?.toUpperCase()?.includes('POSTPONED')) {
      return 'Postponed';
    }

    // Check for halftime status
    if (status === 'STATUS_HALFTIME' || status?.toUpperCase()?.includes('HALFTIME')) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <span style={{ color: 'yellow', fontWeight: 600 }}>Halftime</span>
        </div>
      );
    }

    // Check for statusDetail with shortDetail (ESPN format) - parse into period and clock
    if (safeGame.statusDetail?.type?.shortDetail) {
      const shortDetail = safeGame.statusDetail.type.shortDetail;
      // Try to parse "5:32 - 1st Half" or "5:32 - 2nd" format into two lines
      const match = shortDetail.match(/^([\d:]+)\s*-\s*(.+)$/);
      if (match) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <span style={{ color: 'yellow', fontWeight: 600 }}>{match[2]}</span>
            <span style={{ color: 'yellow', fontWeight: 600 }}>{match[1]}</span>
          </div>
        );
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <span style={{ color: 'yellow', fontWeight: 600 }}>{shortDetail}</span>
        </div>
      );
    }

    // Use period and clock from game data
    if (safeGame.period || safeGame.clock) {
      const periodText = getPeriodText(safeGame.period, sport);

      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <span style={{ color: 'yellow', fontWeight: 600 }}>{periodText}</span>
          {safeGame.clock && (
            <span style={{ color: 'yellow', fontWeight: 600 }}>{safeGame.clock}</span>
          )}
        </div>
      );
    }

    // Default live indicator
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <span style={{ color: 'yellow', fontWeight: 600 }}>LIVE</span>
      </div>
    );
  };

  // =============================================================================
  // PERFORMANCE OPTIMIZATION: Memoized market data processing
  // These computations only re-run when displayData, bookLogos, or sport change
  // To revert: remove this useMemo block and restore inline computations in render
  // =============================================================================
  const processedMarketData = useMemo(() => {
    if (!displayData || displayData.length === 0) {
      return {
        h2hArray: [], h2h3WayRecord: null, spreadsArray: [], totalsArray: [],
        h2hConsensus: null, spreadsConsensus: null, totalsConsensus: null,
        hasH2H: false, hasSpreads: false, hasTotals: false
      };
    }

    const marketContainer = displayData[0] || {};
    const h2hArrayRaw = marketContainer.h2h || [];
    const spreadsArrayRaw = marketContainer.spreads || [];
    const totalsArrayRaw = marketContainer.totals || [];

    // Filter out period markets - only want full game
    const getFullGameMinTotal = () => {
      switch (sport) {
        case 'nfl': return 30;
        case 'ncaaf': return 30;
        case 'nba': return 150;
        case 'wnba': return 120;
        case 'ncaab': return 100;
        default: return 0;
      }
    };
    const minTotalPoint = getFullGameMinTotal();

    const totalsArray = minTotalPoint > 0
      ? totalsArrayRaw.filter(r => r.point >= minTotalPoint)
      : totalsArrayRaw;
    const spreadsArray = spreadsArrayRaw.filter(r => !r.point || r.point <= 30);
    // Pull out the 3-way record so its own block can render against it directly. For
    // non-soccer sports, remove it from h2hArray so the binary rows don't accidentally
    // pick it up via findMainLineForBook (each book's lookup would then read 3-way
    // bestBookPrice and produce a false highlight when its 3-way price matches).
    const h2h3WayRecord = h2hArrayRaw.find(r => r.tierKey === 'h2h_3_way') || null;
    const h2hArray = sport === 'soccer'
      ? h2hArrayRaw
      : h2hArrayRaw.filter(r => r.tierKey !== 'h2h_3_way');

    // Helper functions for consensus computation
    const getWithAlias = (record, prefix, book, suffix) => {
      let value = record?.[`${prefix}_${book}_${suffix}`];
      if (value !== undefined) return value;
      value = record?.[`${prefix}_${suffix}_${book}`];
      if (value !== undefined) return value;
      const aliasBook = REVERSE_ALIASES[book];
      if (aliasBook) {
        value = record?.[`${prefix}_${aliasBook}_${suffix}`];
        if (value !== undefined) return value;
        value = record?.[`${prefix}_${suffix}_${aliasBook}`];
        if (value !== undefined) return value;
      }
      return undefined;
    };

    const getSpreadsValue = (record, book, side) => {
      const checkBook = (b) => {
        if (side === 'away') {
          if (record?.[`spreads_minus_away_${b}_isMain`] === true) return record[`spreads_minus_away_${b}`];
          if (record?.[`spreads_plus_away_${b}_isMain`] === true) return record[`spreads_plus_away_${b}`];
          let value = record?.[`spreads_minus_away_${b}`];
          if (value !== undefined) return value;
          return record?.[`spreads_plus_away_${b}`];
        } else {
          if (record?.[`spreads_minus_home_${b}_isMain`] === true) return record[`spreads_minus_home_${b}`];
          if (record?.[`spreads_plus_home_${b}_isMain`] === true) return record[`spreads_plus_home_${b}`];
          let value = record?.[`spreads_minus_home_${b}`];
          if (value !== undefined) return value;
          return record?.[`spreads_plus_home_${b}`];
        }
      };
      let result = checkBook(book);
      if (result !== undefined) return result;
      const aliasBook = REVERSE_ALIASES[book];
      if (aliasBook) {
        result = checkBook(aliasBook);
        if (result !== undefined) return result;
      }
      return undefined;
    };

    const bookHasData = (record, prefix, book, suffix) => {
      if (prefix === 'spreads') return getSpreadsValue(record, book, suffix) !== undefined;
      return getWithAlias(record, prefix, book, suffix) !== undefined;
    };

    // Consensus main line finder
    const findConsensusMainLine = (array, marketPrefix, books) => {
      if (!array || array.length === 0) return null;
      const recordScores = array.map((record, index) => {
        let score = 0;
        books.forEach(book => {
          const checkBook = (b) => Object.keys(record).some(key =>
            key.startsWith(`${marketPrefix}_`) && key.includes(`_${b}`) &&
            key.endsWith('_isMain') && record[key] === true
          );
          if (checkBook(book)) score++;
          else {
            const aliasBook = REVERSE_ALIASES[book];
            if (aliasBook && checkBook(aliasBook)) score++;
          }
        });
        return { record, index, score };
      });
      recordScores.sort((a, b) => b.score !== a.score ? b.score - a.score : a.index - b.index);
      return recordScores[0]?.score > 0 ? recordScores[0].record : null;
    };

    const hasMainLine = (record, prefix, books) => {
      const hasIsMain = Object.keys(record).some(key =>
        (key.includes(`${prefix}_`) && key.endsWith('_isMain') && record[key] === true) ||
        (key === `${prefix}_isMain` && record[key] === true)
      );
      if (hasIsMain) return true;
      if (record.isMain === true || record[`${prefix}_isMain`] === true) return true;
      return books.some(book => {
        const checkBook = (b) => {
          if (record[`${prefix}_${b}_isAlternate`] === false) return true;
          if (record.isAlternate === false || record[`${prefix}_isAlternate`] === false) return true;
          return Object.keys(record).some(key =>
            key.includes(`${prefix}_`) && key.includes(`_${b}`) &&
            key.endsWith('_isAlternate') && record[key] === false
          );
        };
        if (checkBook(book)) return true;
        const aliasBook = REVERSE_ALIASES[book];
        return aliasBook && checkBook(aliasBook);
      });
    };

    // Pre-compute consensus for each market type
    const h2hConsensus = findConsensusMainLine(h2hArray, 'h2h', bookLogos);
    const spreadsConsensus = findConsensusMainLine(spreadsArray, 'spreads', bookLogos);
    const totalsConsensus = findConsensusMainLine(totalsArray, 'totals', bookLogos);

    // Find main line data for display checks
    const h2hData = h2hArray.find(m => hasMainLine(m, 'h2h', bookLogos)) || {};
    const spreadsData = spreadsArray.find(m => hasMainLine(m, 'spreads', bookLogos)) || {};
    const totalsData = totalsArray.find(m => hasMainLine(m, 'totals', bookLogos)) || {};

    const hasH2H = bookLogos.some(book => bookHasData(h2hData, 'h2h', book, 'home'));
    const hasSpreads = bookLogos.some(book => bookHasData(spreadsData, 'spreads', book, 'away') || bookHasData(spreadsData, 'spreads', book, 'home'));
    const hasTotals = bookLogos.some(book => bookHasData(totalsData, 'totals', book, 'over'));

    return {
      h2hArray, h2h3WayRecord, spreadsArray, totalsArray,
      h2hConsensus, spreadsConsensus, totalsConsensus,
      hasH2H, hasSpreads, hasTotals
    };
  }, [displayData, bookLogos, sport]);
  // =============================================================================

  return (
    <div className="table-group" {...swipeHandlers}>
      {/* Game Header */}
      <GameHeader
        gameTime={gameTime}
          gameDate={gameDate}
        gameStatus={gameStatus}
        game={safeGame}
        getTeamLogoUrl={getTeamLogoUrl}
        isMobile={isMobile}
        isCollapsed={isCollapsed}
        onCollapseToggle={onCollapseToggle}
        formatLiveStatus={formatLiveStatus}
        showScores={true}
        isLoading={showLoading}
      />

      {/* Collapsible content */}
      {!isCollapsed && (
        <div>
          {/* Loading spinner */}
          {showLoading && (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm text-secondary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          {/* No data message */}
          {!showLoading && (!displayData || displayData.length === 0) && (
            <div className="text-center py-4 text-muted">
              <small>No odds available for this game yet.</small>
            </div>
          )}

          {/* Mainline markets display */}
          {!showLoading && displayData && displayData.length > 0 && (() => {
            // =============================================================================
            // PERFORMANCE OPTIMIZATION: Use memoized data from processedMarketData
            // Previously this IIFE recomputed arrays, consensus, and flags on every render
            // To revert: restore original inline computations from git history
            // =============================================================================
            const {
              h2hArray, h2h3WayRecord, spreadsArray, totalsArray,
              h2hConsensus, spreadsConsensus, totalsConsensus,
              hasH2H, hasSpreads, hasTotals
            } = processedMarketData;

            // Helper to get value with alias fallback (caesars -> williamhill_us)
            // Handles both key patterns: {prefix}_{book}_{suffix} and {prefix}_{suffix}_{book}
            const getWithAlias = (record, prefix, book, suffix) => {
              // Try pattern 1: prefix_book_suffix (e.g., h2h_fanduel_home)
              let value = record?.[`${prefix}_${book}_${suffix}`];
              if (value !== undefined) return value;

              // Try pattern 2: prefix_suffix_book (e.g., totals_over_fanduel)
              value = record?.[`${prefix}_${suffix}_${book}`];
              if (value !== undefined) return value;

              // Try alias with pattern 1
              const aliasBook = REVERSE_ALIASES[book];
              if (aliasBook) {
                value = record?.[`${prefix}_${aliasBook}_${suffix}`];
                if (value !== undefined) return value;
                // Try alias with pattern 2
                value = record?.[`${prefix}_${suffix}_${aliasBook}`];
                if (value !== undefined) return value;
              }
              return undefined;
            };

            // Helper for spreads format - finds the main line based on _isMain flag
            // Returns { odds, isFavorite } where isFavorite indicates if this team is giving points (minus)
            const getSpreadsValueWithSign = (record, book, side) => {
              const checkBook = (b) => {
                if (side === 'away') {
                  // Check which pattern has _isMain: true for this book
                  if (record?.[`spreads_minus_away_${b}_isMain`] === true) {
                    return { odds: record[`spreads_minus_away_${b}`], isFavorite: true };
                  }
                  if (record?.[`spreads_plus_away_${b}_isMain`] === true) {
                    return { odds: record[`spreads_plus_away_${b}`], isFavorite: false };
                  }
                  // Fallback: if no _isMain, just check for any value
                  let value = record?.[`spreads_minus_away_${b}`];
                  if (value !== undefined) return { odds: value, isFavorite: true };
                  value = record?.[`spreads_plus_away_${b}`];
                  if (value !== undefined) return { odds: value, isFavorite: false };
                } else {
                  // Check which pattern has _isMain: true for this book
                  if (record?.[`spreads_minus_home_${b}_isMain`] === true) {
                    return { odds: record[`spreads_minus_home_${b}`], isFavorite: true };
                  }
                  if (record?.[`spreads_plus_home_${b}_isMain`] === true) {
                    return { odds: record[`spreads_plus_home_${b}`], isFavorite: false };
                  }
                  // Fallback: if no _isMain, just check for any value
                  let value = record?.[`spreads_minus_home_${b}`];
                  if (value !== undefined) return { odds: value, isFavorite: true };
                  value = record?.[`spreads_plus_home_${b}`];
                  if (value !== undefined) return { odds: value, isFavorite: false };
                }
                return null;
              };

              let result = checkBook(book);
              if (result) return result;

              // Try alias
              const aliasBook = REVERSE_ALIASES[book];
              if (aliasBook) {
                result = checkBook(aliasBook);
                if (result) return result;
              }

              // Fallback to old format
              const oldValue = getWithAlias(record, 'spreads', book, side);
              if (oldValue !== undefined) {
                // Check old format _isAlternate flags to determine favorite/underdog
                const isAlt = record?.[`spreads_${book}_isAlternate`];
                return { odds: oldValue, isFavorite: side === 'home' };
              }

              return null;
            };

            // Simple helper that just returns odds value (for backward compatibility)
            const getSpreadsValue = (record, book, side) => {
              const result = getSpreadsValueWithSign(record, book, side);
              return result?.odds;
            };

            // Helper to check if book has data (with alias fallback)
            const bookHasData = (record, prefix, book, suffix) => {
              if (prefix === 'spreads') {
                return getSpreadsValue(record, book, suffix) !== undefined;
              }
              return getWithAlias(record, prefix, book, suffix) !== undefined;
            };

            // Helper to check if record has any _isMain = true for a book and side
            const hasIsMain = (record, prefix, book, side) => {
              // For spreads, check all possible patterns:
              // spreads_minus_away_{book}, spreads_plus_away_{book}, spreads_minus_home_{book}, spreads_plus_home_{book}
              if (prefix === 'spreads') {
                if (side === 'away') {
                  return record[`spreads_plus_away_${book}_isMain`] === true ||
                         record[`spreads_minus_away_${book}_isMain`] === true;
                } else {
                  return record[`spreads_minus_home_${book}_isMain`] === true ||
                         record[`spreads_plus_home_${book}_isMain`] === true;
                }
              }
              // For h2h and totals, check both patterns:
              // Pattern 1: {prefix}_{book}_{side}_isMain (e.g., h2h_fanduel_home_isMain)
              // Pattern 2: {prefix}_{side}_{book}_isMain (e.g., totals_over_fanduel_isMain)
              return record[`${prefix}_${book}_${side}_isMain`] === true ||
                     record[`${prefix}_${side}_${book}_isMain`] === true;
            };

            // Helper to find the main line record for a specific book and side
            // Note: h2hConsensus, spreadsConsensus, totalsConsensus come from memoized processedMarketData
            // Uses pre-computed consensus, then falls back to book-specific main line
            const findMainLineForBook = (array, marketPrefix, book, side = null) => {
              // Use pre-computed consensus based on market type
              const consensus = marketPrefix === 'h2h' ? h2hConsensus :
                               marketPrefix === 'spreads' ? spreadsConsensus :
                               marketPrefix === 'totals' ? totalsConsensus : null;

              // If consensus exists and this book has data at that point, use it
              if (consensus) {
                if (side) {
                  if (bookHasData(consensus, marketPrefix, book, side)) {
                    return consensus;
                  }
                } else {
                  // Check if book has any data in this consensus record
                  const hasAnyData = Object.keys(consensus).some(key =>
                    key.startsWith(`${marketPrefix}_`) &&
                    (key.includes(`_${book}`) || key.includes(`_${REVERSE_ALIASES[book] || 'noalias'}`)) &&
                    !key.endsWith('_isMain') &&
                    !key.endsWith('_isAlternate') &&
                    consensus[key] !== undefined
                  );
                  if (hasAnyData) return consensus;
                }
              }

              // Fall back to finding where this specific book has _isMain
              return array.find(m => {
                // Check if this record has _isMain for this book
                const checkBookIsMain = (b) => {
                  if (side) {
                    return hasIsMain(m, marketPrefix, b, side);
                  }
                  // If no side specified, check if any _isMain exists for this book
                  return Object.keys(m).some(key =>
                    key.startsWith(`${marketPrefix}_`) &&
                    key.includes(`_${b}`) &&
                    key.endsWith('_isMain') &&
                    m[key] === true
                  );
                };

                // Check for new _isMain format
                if (checkBookIsMain(book)) return true;
                const aliasBook = REVERSE_ALIASES[book];
                if (aliasBook && checkBookIsMain(aliasBook)) return true;

                // Fallback: check for old _isAlternate === false format
                // Check both patterns: {prefix}_{book}_isAlternate and per-side patterns
                const checkIsAlternateFalse = (b) => {
                  // Pattern 1: prefix_book_isAlternate
                  if (m[`${marketPrefix}_${b}_isAlternate`] === false) return true;
                  // Check if any per-side isAlternate is false for this book
                  return Object.keys(m).some(key =>
                    key.startsWith(`${marketPrefix}_`) &&
                    key.includes(`_${b}`) &&
                    key.endsWith('_isAlternate') &&
                    m[key] === false
                  );
                };
                const hasIsAlternateFalse = checkIsAlternateFalse(book) ||
                  (aliasBook && checkIsAlternateFalse(aliasBook));
                if (hasIsAlternateFalse) {
                  if (side) {
                    return bookHasData(m, marketPrefix, book, side);
                  }
                  return true;
                }

                return false;
              }) || null;
            };

            // Helper to check if record has _isMain true for any book
            // Falls back to checking _isAlternate === false for backward compatibility
            const hasMainLine = (record, prefix, books) => {
              // First check for new _isMain format - check all possible patterns
              const hasIsMain = Object.keys(record).some(key => {
                // Match patterns like: spreads_fanduel_isMain, spreads_plus_away_fanduel_isMain, totals_fanduel_over_isMain
                if (key.includes(`${prefix}_`) && key.endsWith('_isMain') && record[key] === true) {
                  return true;
                }
                // Also check the _isMain flag on the record itself
                if (key === `${prefix}_isMain` && record[key] === true) {
                  return true;
                }
                return false;
              });
              if (hasIsMain) return true;

              // Check for record-level isMain flag
              if (record.isMain === true || record[`${prefix}_isMain`] === true) {
                return true;
              }

              // Fallback: check for old _isAlternate === false format
              // Check both patterns and per-side patterns
              return books.some(book => {
                const checkBook = (b) => {
                  // Pattern 1: prefix_book_isAlternate
                  if (record[`${prefix}_${b}_isAlternate`] === false) return true;
                  // Pattern 2: check for record-level isAlternate
                  if (record.isAlternate === false || record[`${prefix}_isAlternate`] === false) return true;
                  // Check if any per-side isAlternate is false for this book
                  return Object.keys(record).some(key =>
                    key.includes(`${prefix}_`) &&
                    key.includes(`_${b}`) &&
                    key.endsWith('_isAlternate') &&
                    record[key] === false
                  );
                };
                if (checkBook(book)) return true;
                const aliasBook = REVERSE_ALIASES[book];
                if (aliasBook && checkBook(aliasBook)) return true;
                return false;
              });
            };

            // Note: h2hData, spreadsData, totalsData, hasH2H, hasSpreads, hasTotals
            // now come from memoized processedMarketData (destructured at top of IIFE)

            // Mobile consolidated view - show best book/odds for each market in one table
            if (isMobile) {
              // Compute best book by finding the main line with best edge
              const computeBestBook = (array, marketPrefix, side, books) => {
                // Find all main line records and pick the one with the best edge
                const mainRecords = array.filter(m => hasMainLine(m, marketPrefix, books));

                let bestBook = null;
                let bestOdds = -Infinity;
                let bestDiff = 0;
                let bestPoint = null;
                let diffBelongsToSelectedBook = false;

                // Helper to check if a book's odds are available
                const isAvailable = (record, book, side) => {
                  // Check both patterns for isAvailable flag
                  const flag1 = record[`${marketPrefix}_${book}_${side}_isAvailable`];
                  const flag2 = record[`${marketPrefix}_${side}_${book}_isAvailable`];
                  // Also check alias
                  const aliasBook = REVERSE_ALIASES[book];
                  const flag3 = aliasBook ? record[`${marketPrefix}_${aliasBook}_${side}_isAvailable`] : undefined;
                  const flag4 = aliasBook ? record[`${marketPrefix}_${side}_${aliasBook}_isAvailable`] : undefined;
                  // If any flag is explicitly false, not available
                  if (flag1 === false || flag2 === false || flag3 === false || flag4 === false) {
                    return false;
                  }
                  return true;
                };

                // Helper to check if a book is the best book for this diff
                const isBestBookForDiff = (record, book, side) => {
                  // Check for bestBook field: marketPrefix_bestBook_side
                  const bestBookField = record[`${marketPrefix}_bestBook_${side}`];
                  if (bestBookField) {
                    const aliasBook = REVERSE_ALIASES[book];
                    return bestBookField === book || bestBookField === aliasBook;
                  }
                  return false;
                };

                // Check each main line record for the best edge
                mainRecords.forEach(record => {
                  const recordDiff = record[`${marketPrefix}_bestBookDiff_${side}`] || 0;

                  // If this record has a better edge, check its odds
                  books.forEach(book => {
                    // Skip if this book's odds are not available
                    if (!isAvailable(record, book, side)) return;

                    const odds = getWithAlias(record, marketPrefix, book, side);
                    if (odds !== undefined && odds !== null && typeof odds === 'number') {
                      // Prioritize by edge, then by odds value
                      if (recordDiff > bestDiff || (recordDiff === bestDiff && odds > bestOdds)) {
                        bestOdds = odds;
                        bestBook = book;
                        bestDiff = recordDiff;
                        bestPoint = record.point;
                        // Only use diff for tier coloring if this book is actually the best book
                        diffBelongsToSelectedBook = isBestBookForDiff(record, book, side);
                      }
                    }
                  });
                });

                if (!bestBook || bestOdds === -Infinity) return null;
                // Only include diff if it belongs to the selected book
                return { book: bestBook, odds: bestOdds, diff: diffBelongsToSelectedBook ? bestDiff : 0, point: bestPoint };
              };

              // Compute best book for spreads (handles plus/minus) - finds main line with best edge
              const computeSpreadBestBook = (array, side, books) => {
                const mainRecords = array.filter(m => hasMainLine(m, 'spreads', books));

                let bestBook = null;
                let bestOdds = -Infinity;
                let bestDiff = 0;
                let bestSign = 'plus';
                let bestPoint = null;
                let diffBelongsToSelectedBook = false;

                // Helper to check if spread odds are available
                const isSpreadAvailable = (record, b, sign) => {
                  const flag = record[`spreads_${sign}_${side}_${b}_isAvailable`];
                  return flag !== false;
                };

                // Helper to check if this specific sign/side combo is the main line
                const isSignMain = (record, b, sign) => {
                  // Check for explicit _isMain flag: spreads_{sign}_{side}_{book}_isMain
                  const isMainKey = `spreads_${sign}_${side}_${b}_isMain`;
                  if (record[isMainKey] === true) return true;
                  if (record[isMainKey] === false) return false;

                  // Check for explicit _isAlternate flag
                  const isAltKey = `spreads_${sign}_${side}_${b}_isAlternate`;
                  if (record[isAltKey] === true) return false;
                  if (record[isAltKey] === false) return true;

                  // Check alias book
                  const aliasBook = REVERSE_ALIASES[b];
                  if (aliasBook) {
                    const aliasMainKey = `spreads_${sign}_${side}_${aliasBook}_isMain`;
                    if (record[aliasMainKey] === true) return true;
                    if (record[aliasMainKey] === false) return false;

                    const aliasAltKey = `spreads_${sign}_${side}_${aliasBook}_isAlternate`;
                    if (record[aliasAltKey] === true) return false;
                    if (record[aliasAltKey] === false) return true;
                  }

                  // Check if the other sign for this side is explicitly main - if so, this one isn't
                  const otherSign = sign === 'plus' ? 'minus' : 'plus';
                  const otherMainKey = `spreads_${otherSign}_${side}_${b}_isMain`;
                  if (record[otherMainKey] === true) return false;
                  if (aliasBook) {
                    const otherAliasMainKey = `spreads_${otherSign}_${side}_${aliasBook}_isMain`;
                    if (record[otherAliasMainKey] === true) return false;
                  }

                  // Default: if no flags found, allow (record passed main line filter)
                  return true;
                };

                // Helper to check if a book is the best book for this diff/sign
                const isBestBookForDiff = (record, book, sign) => {
                  // Check for bestBook field: spreads_bestBook_{sign}_{side}
                  const bestBookField = record[`spreads_bestBook_${sign}_${side}`];
                  if (bestBookField) {
                    const aliasBook = REVERSE_ALIASES[book];
                    return bestBookField === book || bestBookField === aliasBook;
                  }
                  return false;
                };

                mainRecords.forEach(record => {
                  const plusDiff = record[`spreads_bestBookDiff_plus_${side}`] || 0;
                  const minusDiff = record[`spreads_bestBookDiff_minus_${side}`] || 0;

                  books.forEach(book => {
                    const checkBook = (b) => {
                      // Check plus (underdog) - skip if not available or not main for this side
                      if (isSpreadAvailable(record, b, 'plus') && isSignMain(record, b, 'plus')) {
                        const plusKey = `spreads_plus_${side}_${b}`;
                        const plusOdds = record[plusKey];
                        if (plusOdds !== undefined && plusOdds !== null && typeof plusOdds === 'number') {
                          if (plusDiff > bestDiff || (plusDiff === bestDiff && plusOdds > bestOdds)) {
                            bestOdds = plusOdds;
                            bestBook = book;
                            bestSign = 'plus';
                            bestDiff = plusDiff;
                            bestPoint = record.point;
                            diffBelongsToSelectedBook = isBestBookForDiff(record, book, 'plus');
                          }
                        }
                      }
                      // Check minus (favorite) - skip if not available or not main for this side
                      if (isSpreadAvailable(record, b, 'minus') && isSignMain(record, b, 'minus')) {
                        const minusKey = `spreads_minus_${side}_${b}`;
                        const minusOdds = record[minusKey];
                        if (minusOdds !== undefined && minusOdds !== null && typeof minusOdds === 'number') {
                          if (minusDiff > bestDiff || (minusDiff === bestDiff && minusOdds > bestOdds)) {
                            bestOdds = minusOdds;
                            bestBook = book;
                            bestSign = 'minus';
                            bestDiff = minusDiff;
                            bestPoint = record.point;
                            diffBelongsToSelectedBook = isBestBookForDiff(record, book, 'minus');
                          }
                        }
                      }
                    };

                    checkBook(book);
                    const aliasBook = REVERSE_ALIASES[book];
                    if (aliasBook) checkBook(aliasBook);
                  });
                });

                if (!bestBook || bestOdds === -Infinity) return null;
                // Only include diff if it belongs to the selected book
                return { book: bestBook, odds: bestOdds, diff: diffBelongsToSelectedBook ? bestDiff : 0, sign: bestSign, point: bestPoint };
              };

              const awayH2H = computeBestBook(h2hArray, 'h2h', 'away', allBookmakers);
              const homeH2H = computeBestBook(h2hArray, 'h2h', 'home', allBookmakers);
              const awaySpread = computeSpreadBestBook(spreadsArray, 'away', allBookmakers);
              const homeSpread = computeSpreadBestBook(spreadsArray, 'home', allBookmakers);
              const overTotal = computeBestBook(totalsArray, 'totals', 'over', allBookmakers);
              const underTotal = computeBestBook(totalsArray, 'totals', 'under', allBookmakers);

              // Returns { bookCell, valueCell } for split rendering
              const renderBestCellParts = (info, extraLabel, inline = false) => {
                if (!info) {
                  return {
                    bookCell: null,
                    valueCell: <span style={{ color: '#6c757d' }}>-</span>
                  };
                }
                const isMulti = info.book === 'multi';
                const bookDisplay = isMulti ? null : info.book;
                const hasDiff = info.diff && Math.abs(info.diff) >= 0.05;

                // Determine tier colors
                let bgColor = 'transparent';
                let textColor = hasDiff ? '#F59E0B' : undefined;
                if (hasDiff && !isMulti) {
                  const diffValue = Math.abs(info.diff);
                  if (diffValue >= 0.25) {
                    bgColor = 'green';
                    textColor = 'yellow';
                  } else if (diffValue >= 0.15) {
                    bgColor = '#F59E0B';
                    textColor = '#1E40AF';
                  } else if (diffValue >= 0.05) {
                    bgColor = '#3B82F6';
                    textColor = 'white';
                  }
                }

                const bookCell = bookDisplay ? (
                  <img
                    src={`/logos/${bookDisplay}.png`}
                    alt={bookDisplay}
                    style={{ width: '16px', height: '16px' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : null;

                const valueCell = (
                  <div style={{
                    display: 'flex',
                    flexDirection: inline ? 'row' : 'column',
                    alignItems: 'center',
                    gap: inline ? '3px' : '1px',
                    padding: '2px 4px',
                    borderRadius: '4px',
                    backgroundColor: bgColor
                  }}>
                    {extraLabel && <small style={{ color: textColor, fontSize: '0.6rem', whiteSpace: 'nowrap' }}>{extraLabel}</small>}
                    <span style={{
                      fontWeight: hasDiff ? 'bold' : 'normal',
                      color: textColor,
                      fontSize: '0.85rem'
                    }}>
                      {info.odds > 0 ? '+' : ''}{info.odds}
                    </span>
                  </div>
                );

                return { bookCell, valueCell };
              };

              // Get cell parts for all markets
              const awayH2HParts = renderBestCellParts(awayH2H);
              const homeH2HParts = renderBestCellParts(homeH2H);
              const awaySpreadParts = renderBestCellParts(awaySpread, awaySpread?.point ? `${awaySpread.sign === 'minus' ? '-' : '+'}${awaySpread.point}` : null);
              const homeSpreadParts = renderBestCellParts(homeSpread, homeSpread?.point ? `${homeSpread.sign === 'minus' ? '-' : '+'}${homeSpread.point}` : null);
              const overTotalParts = renderBestCellParts(overTotal, overTotal?.point ? `o${overTotal.point}` : 'o');
              const underTotalParts = renderBestCellParts(underTotal, underTotal?.point ? `u${underTotal.point}` : 'u');

              // Cell styles for book logo cells (right border removed, clickable)
              const bookCellStyle = {
                verticalAlign: 'middle',
                padding: '4px 2px 4px 4px',
                borderRight: 0,
                width: '24px',
                cursor: 'pointer'
              };
              // Cell styles for value cells (left border removed, clickable)
              const valueCellStyle = {
                verticalAlign: 'middle',
                padding: '4px 4px 4px 2px',
                borderLeft: 0,
                cursor: 'pointer'
              };

              return (
                <div className="table-container">
                  <Table bordered size="sm" className="text-center" style={{ fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#1a1a2e' }}>
                        <th style={{ width: '22%' }}>Team</th>
                        <th colSpan={2} style={{ width: '26%' }}>{safeGame.sport?.toLowerCase() === 'nfl' || safeGame.sport?.toLowerCase() === 'nba' ? 'Game' : 'ML'}</th>
                        <th colSpan={2} style={{ width: '26%' }}>Spread</th>
                        <th colSpan={2} style={{ width: '26%' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td
                          style={{ verticalAlign: 'middle', textAlign: 'left', padding: '6px', cursor: 'pointer' }}
                          onClick={() => setShowGameModal(true)}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <img
                              src={safeGame.awayLogo || getTeamLogoUrl(safeGame.away)}
                              alt={safeGame.away}
                              style={{ width: '18px', height: '18px' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <span style={{ fontSize: '0.75rem', fontWeight: '500' }}>{safeGame.away}</span>
                          </div>
                        </td>
                        {/* ML */}
                        <td style={bookCellStyle} onClick={() => setShowGameModal(true)}>{awayH2HParts.bookCell}</td>
                        <td style={valueCellStyle} onClick={() => setShowGameModal(true)}>{awayH2HParts.valueCell}</td>
                        {/* Spread */}
                        <td style={bookCellStyle} onClick={() => setShowGameModal(true)}>{awaySpreadParts.bookCell}</td>
                        <td style={valueCellStyle} onClick={() => setShowGameModal(true)}>{awaySpreadParts.valueCell}</td>
                        {/* Total */}
                        <td style={bookCellStyle} onClick={() => setShowGameModal(true)}>{overTotalParts.bookCell}</td>
                        <td style={valueCellStyle} onClick={() => setShowGameModal(true)}>{overTotalParts.valueCell}</td>
                      </tr>
                      <tr>
                        <td
                          style={{ verticalAlign: 'middle', textAlign: 'left', padding: '6px', cursor: 'pointer' }}
                          onClick={() => setShowGameModal(true)}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <img
                              src={safeGame.homeLogo || getTeamLogoUrl(safeGame.home)}
                              alt={safeGame.home}
                              style={{ width: '18px', height: '18px' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <span style={{ fontSize: '0.75rem', fontWeight: '500' }}>{safeGame.home}</span>
                          </div>
                        </td>
                        {/* ML */}
                        <td style={bookCellStyle} onClick={() => setShowGameModal(true)}>{homeH2HParts.bookCell}</td>
                        <td style={valueCellStyle} onClick={() => setShowGameModal(true)}>{homeH2HParts.valueCell}</td>
                        {/* Spread */}
                        <td style={bookCellStyle} onClick={() => setShowGameModal(true)}>{homeSpreadParts.bookCell}</td>
                        <td style={valueCellStyle} onClick={() => setShowGameModal(true)}>{homeSpreadParts.valueCell}</td>
                        {/* Total */}
                        <td style={bookCellStyle} onClick={() => setShowGameModal(true)}>{underTotalParts.bookCell}</td>
                        <td style={valueCellStyle} onClick={() => setShowGameModal(true)}>{underTotalParts.valueCell}</td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              );
            }

            // Desktop view - show separate tables for each market type
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Moneyline Table */}
                {hasH2H && (
                  <div className="table-container">
                    <h6 className="text-center mb-2" style={{ fontSize: '0.9rem', fontWeight: 'bold', color: 'yellow' }}>
                      Moneyline
                    </h6>
                    <Table bordered size="sm" className="text-center">
                      <thead>
                        <tr>
                          <th style={{ width: '120px' }}>TEAM</th>
                          {bookLogos.map(book => (
                            <th key={`h2h-${book}`} style={oddsCellStyle}>
                              <img
                                src={getBookLogoUrl(book)}
                                alt={`${book} logo`}
                                title={book}
                                style={{ height: '20px' }}
                              />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {/* Away Team Row */}
                        <tr>
                          <td
                            style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                            onClick={() => setShowGameModal(true)}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                              <img
                                src={safeGame.awayLogo || getTeamLogoUrl(safeGame.away)}
                                alt={safeGame.away}
                                style={{ width: '20px', height: '20px' }}
                                onError={(e) => {
                                  e.target.style.visibility = 'hidden';
                                  e.target.style.width = '0px';
                                  e.target.style.height = '0px';
                                }}
                              />
                              {safeGame.away}
                            </div>
                          </td>
                          {(() => {
                            // Count how many books have the best price for h2h away
                            const allOdds = bookLogos.map(b => {
                              const rec = findMainLineForBook(h2hArray, 'h2h', b);
                              return getWithAlias(rec, 'h2h', b, 'away');
                            }).filter(o => o !== undefined && o !== null);
                            const bestPrice = allOdds.length > 0 ? Math.max(...allOdds) : null;
                            const booksWithBestPrice = allOdds.filter(o => o === bestPrice).length;

                            return bookLogos.map(book => {
                              const bookRecord = findMainLineForBook(h2hArray, 'h2h', book);
                              const odds = getWithAlias(bookRecord, 'h2h', book, 'away');
                              const bestBookPrice = bookRecord?.['h2h_bestBookPrice_away'];
                              const bestBookDiff = bookRecord?.['h2h_bestBookDiff_away'];
                              const cellStyle = odds ? getCellStyle(odds, bestBookPrice, bestBookDiff, booksWithBestPrice) : {};

                              return (
                                <td key={`h2h-${book}-away`} style={{...oddsCellStyle, padding: '4px', cursor: odds ? 'pointer' : undefined}}
                                  onClick={() => odds && onOddsClick && onOddsClick(safeGame.gameIDmlb, null, safeGame.away, 'h2h', book, odds, safeGame.away, safeGame.home, safeGame.away, null, null, 'away', safeGame.awayTeamId)}
                                >
                                  {odds ? (
                                    <div style={{ ...cellStyle, padding: '4px', borderRadius: '3px', fontSize: '0.9rem' }}>
                                      {odds > 0 ? `+${odds}` : odds}
                                    </div>
                                  ) : null}
                                </td>
                              );
                            });
                          })()}
                        </tr>
                        {/* Home Team Row */}
                        <tr>
                          <td
                            style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                            onClick={() => setShowGameModal(true)}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                              <img
                                src={safeGame.homeLogo || getTeamLogoUrl(safeGame.home)}
                                alt={safeGame.home}
                                style={{ width: '20px', height: '20px' }}
                                onError={(e) => {
                                  e.target.style.visibility = 'hidden';
                                  e.target.style.width = '0px';
                                  e.target.style.height = '0px';
                                }}
                              />
                              {safeGame.home}
                            </div>
                          </td>
                          {(() => {
                            // Count how many books have the best price for h2h home
                            const allOdds = bookLogos.map(b => {
                              const rec = findMainLineForBook(h2hArray, 'h2h', b);
                              return getWithAlias(rec, 'h2h', b, 'home');
                            }).filter(o => o !== undefined && o !== null);
                            const bestPrice = allOdds.length > 0 ? Math.max(...allOdds) : null;
                            const booksWithBestPrice = allOdds.filter(o => o === bestPrice).length;

                            return bookLogos.map(book => {
                              const bookRecord = findMainLineForBook(h2hArray, 'h2h', book);
                              const odds = getWithAlias(bookRecord, 'h2h', book, 'home');
                              const bestBookPrice = bookRecord?.['h2h_bestBookPrice_home'];
                              const bestBookDiff = bookRecord?.['h2h_bestBookDiff_home'];
                              const cellStyle = odds ? getCellStyle(odds, bestBookPrice, bestBookDiff, booksWithBestPrice) : {};

                              return (
                                <td key={`h2h-${book}-home`} style={{...oddsCellStyle, padding: '4px', cursor: odds ? 'pointer' : undefined}}
                                  onClick={() => odds && onOddsClick && onOddsClick(safeGame.gameIDmlb, null, safeGame.home, 'h2h', book, odds, safeGame.away, safeGame.home, safeGame.home, null, null, 'home', safeGame.homeTeamId)}
                                >
                                  {odds ? (
                                    <div style={{ ...cellStyle, padding: '4px', borderRadius: '3px', fontSize: '0.9rem' }}>
                                      {odds > 0 ? `+${odds}` : odds}
                                    </div>
                                  ) : null}
                                </td>
                              );
                            });
                          })()}
                        </tr>
                        {/* 3-Way h2h rows — separate from binary so each book's bestBookPrice
                            comes from the same row across all cells (avoids spurious highlights).
                            Hidden for now while the underlying 3-way data is being verified. */}
                        {(() => {
                          const SHOW_3WAY_ROWS = false;
                          if (!SHOW_3WAY_ROWS || !h2h3WayRecord) return null;

                          const render3WayRow = (side, labelNode) => {
                            const allOdds = bookLogos
                              .map(b => getWithAlias(h2h3WayRecord, 'h2h', b, side))
                              .filter(o => o !== undefined && o !== null);
                            const bestPrice = allOdds.length > 0 ? Math.max(...allOdds) : null;
                            const booksWithBestPrice = allOdds.filter(o => o === bestPrice).length;
                            const bestBookPrice = h2h3WayRecord[`h2h_bestBookPrice_${side}`];
                            const bestBookDiff = h2h3WayRecord[`h2h_bestBookDiff_${side}`];

                            return (
                              <tr key={`h2h-3way-${side}`}>
                                <td style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold' }}>
                                  {labelNode}
                                </td>
                                {bookLogos.map(book => {
                                  const odds = getWithAlias(h2h3WayRecord, 'h2h', book, side);
                                  const cellStyle = odds ? getCellStyle(odds, bestBookPrice, bestBookDiff, booksWithBestPrice) : {};
                                  return (
                                    <td key={`h2h-3way-${book}-${side}`} style={{ ...oddsCellStyle, padding: '4px', cursor: odds ? 'pointer' : undefined }}
                                      onClick={() => odds && onOddsClick && onOddsClick(safeGame.gameIDmlb, null, side === 'draw' ? 'Draw' : (side === 'away' ? safeGame.away : safeGame.home), 'h2h_3_way', book, odds, safeGame.away, safeGame.home, side === 'away' ? safeGame.away : side === 'home' ? safeGame.home : 'Draw', null, null, side, side === 'away' ? safeGame.awayTeamId : side === 'home' ? safeGame.homeTeamId : null)}
                                    >
                                      {odds ? (
                                        <div style={{ ...cellStyle, padding: '4px', borderRadius: '3px', fontSize: '0.9rem' }}>
                                          {odds > 0 ? `+${odds}` : odds}
                                        </div>
                                      ) : null}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          };

                          const awayLabel = (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                              <img
                                src={safeGame.awayLogo || getTeamLogoUrl(safeGame.away)}
                                alt={safeGame.away}
                                style={{ width: '20px', height: '20px' }}
                                onError={(e) => { e.target.style.visibility = 'hidden'; e.target.style.width = '0px'; e.target.style.height = '0px'; }}
                              />
                              <span>{safeGame.away}</span>
                              <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>(3-way)</span>
                            </div>
                          );
                          const drawLabel = (
                            <span>Draw <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>(3-way)</span></span>
                          );
                          const homeLabel = (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                              <img
                                src={safeGame.homeLogo || getTeamLogoUrl(safeGame.home)}
                                alt={safeGame.home}
                                style={{ width: '20px', height: '20px' }}
                                onError={(e) => { e.target.style.visibility = 'hidden'; e.target.style.width = '0px'; e.target.style.height = '0px'; }}
                              />
                              <span>{safeGame.home}</span>
                              <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>(3-way)</span>
                            </div>
                          );

                          return (
                            <>
                              <tr aria-hidden="true">
                                <td colSpan={bookLogos.length + 1} style={{ height: '8px', padding: 0, border: 'none' }} />
                              </tr>
                              {render3WayRow('away', awayLabel)}
                              {render3WayRow('draw', drawLabel)}
                              {render3WayRow('home', homeLabel)}
                            </>
                          );
                        })()}
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* Totals Table */}
                {hasTotals && (
                  <div className="table-container">
                    <Table bordered size="sm" className="text-center">
                      <thead>
                        <tr><th colSpan={bookLogos.length + 1} style={{ backgroundColor: 'transparent', color: 'yellow', fontWeight: 'bold' }}>Totals</th></tr>
                        <tr>
                          <th style={{ width: '120px' }}></th>
                          {bookLogos.map(book => (
                            <th key={`totals-${book}`} style={oddsCellStyle}>
                              <img
                                src={getBookLogoUrl(book)}
                                alt={`${book} logo`}
                                title={book}
                                style={{ height: '20px' }}
                              />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {/* Over Row */}
                        <tr>
                          <td style={{ width: '120px', fontWeight: 'bold' }}>Over</td>
                          {(() => {
                            // Count how many books have the best price for totals over
                            const allOdds = bookLogos.map(b => {
                              const rec = findMainLineForBook(totalsArray, 'totals', b);
                              return getWithAlias(rec, 'totals', b, 'over');
                            }).filter(o => o !== undefined && o !== null);
                            const bestPrice = allOdds.length > 0 ? Math.max(...allOdds) : null;
                            const booksWithBestPrice = allOdds.filter(o => o === bestPrice).length;

                            return bookLogos.map(book => {
                              const bookRecord = findMainLineForBook(totalsArray, 'totals', book);
                              const odds = getWithAlias(bookRecord, 'totals', book, 'over');
                              const point = bookRecord?.point;
                              const bestBookPrice = bookRecord?.['totals_bestBookPrice_over'];
                              const bestBookDiff = bookRecord?.['totals_bestBookDiff_over'];
                              const cellStyle = odds ? getCellStyle(odds, bestBookPrice, bestBookDiff, booksWithBestPrice) : {};

                              return (
                                <td key={`totals-${book}-over`} style={{...oddsCellStyle, textAlign: 'center', padding: '4px', cursor: odds ? 'pointer' : undefined}}
                                  onClick={() => odds && onOddsClick && onOddsClick(safeGame.gameIDmlb, null, 'Over', 'totals', book, odds, safeGame.away, safeGame.home, null, null, point || null, 'over')}
                                >
                                  {odds ? (
                                    <div style={{ ...cellStyle, padding: '4px', borderRadius: '3px' }}>
                                      <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{odds > 0 ? `+${odds}` : odds}</div>
                                      {point && <div style={{ fontSize: '0.75rem', color: cellStyle.color || '#6c757d' }}>o{point}</div>}
                                    </div>
                                  ) : null}
                                </td>
                              );
                            });
                          })()}
                        </tr>

                        {/* Under Row */}
                        <tr>
                          <td style={{ width: '120px', fontWeight: 'bold' }}>Under</td>
                          {(() => {
                            // Count how many books have the best price for totals under
                            const allOdds = bookLogos.map(b => {
                              const rec = findMainLineForBook(totalsArray, 'totals', b);
                              return getWithAlias(rec, 'totals', b, 'under');
                            }).filter(o => o !== undefined && o !== null);
                            const bestPrice = allOdds.length > 0 ? Math.max(...allOdds) : null;
                            const booksWithBestPrice = allOdds.filter(o => o === bestPrice).length;

                            return bookLogos.map(book => {
                              const bookRecord = findMainLineForBook(totalsArray, 'totals', book);
                              const odds = getWithAlias(bookRecord, 'totals', book, 'under');
                              const point = bookRecord?.point;
                              const bestBookPrice = bookRecord?.['totals_bestBookPrice_under'];
                              const bestBookDiff = bookRecord?.['totals_bestBookDiff_under'];
                              const cellStyle = odds ? getCellStyle(odds, bestBookPrice, bestBookDiff, booksWithBestPrice) : {};

                              return (
                                <td key={`totals-${book}-under`} style={{...oddsCellStyle, textAlign: 'center', padding: '4px', cursor: odds ? 'pointer' : undefined}}
                                  onClick={() => odds && onOddsClick && onOddsClick(safeGame.gameIDmlb, null, 'Under', 'totals', book, odds, safeGame.away, safeGame.home, null, null, point || null, 'under')}
                                >
                                  {odds ? (
                                    <div style={{ ...cellStyle, padding: '4px', borderRadius: '3px' }}>
                                      {point && <div style={{ fontSize: '0.75rem', color: cellStyle.color || '#6c757d' }}>u{point}</div>}
                                      <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{odds > 0 ? `+${odds}` : odds}</div>
                                    </div>
                                  ) : null}
                                </td>
                              );
                            });
                          })()}
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* Spreads Table */}
                {hasSpreads && (
                  <div className="table-container">
                    <Table bordered size="sm" className="text-center">
                      <thead>
                        <tr><th colSpan={bookLogos.length + 1} style={{ backgroundColor: 'transparent', color: 'yellow', fontWeight: 'bold' }}>Spreads</th></tr>
                        <tr>
                          <th style={{ width: '120px' }}></th>
                          {bookLogos.map(book => (
                            <th key={`spreads-${book}`} style={oddsCellStyle}>
                              <img
                                src={getBookLogoUrl(book)}
                                alt={`${book} logo`}
                                title={book}
                                style={{ height: '20px' }}
                              />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {/* Away Team Row */}
                        <tr>
                          <td
                            style={{ width: '120px', fontWeight: 'bold', cursor: 'pointer' }}
                            onClick={() => setShowGameModal(true)}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                              <img
                                src={safeGame.awayLogo || getTeamLogoUrl(safeGame.away)}
                                alt={safeGame.away}
                                style={{ width: '16px', height: '16px' }}
                                onError={(e) => {
                                  e.target.style.visibility = 'hidden';
                                  e.target.style.width = '0px';
                                  e.target.style.height = '0px';
                                  e.target.style.margin = '0px';
                                }}
                              />
                              <span>{safeGame.away}</span>
                            </div>
                          </td>
                          {(() => {
                            // Count how many books have the best price for spreads away
                            const allOdds = bookLogos.map(b => {
                              const rec = findMainLineForBook(spreadsArray, 'spreads', b, 'away');
                              return getSpreadsValue(rec, b, 'away');
                            }).filter(o => o !== undefined && o !== null);
                            const bestPrice = allOdds.length > 0 ? Math.max(...allOdds) : null;
                            const booksWithBestPrice = allOdds.filter(o => o === bestPrice).length;

                            return bookLogos.map(book => {
                              const bookRecord = findMainLineForBook(spreadsArray, 'spreads', book, 'away');
                              const spreadInfo = getSpreadsValueWithSign(bookRecord, book, 'away');
                              const odds = spreadInfo?.odds;
                              const isFavorite = spreadInfo?.isFavorite;
                              const point = bookRecord?.point;
                              const bestBookPrice = bookRecord?.['spreads_bestBookPrice_plus_away'];
                              const bestBookDiff = bookRecord?.['spreads_bestBookDiff_plus_away'];
                              const cellStyle = odds ? getCellStyle(odds, bestBookPrice, bestBookDiff, booksWithBestPrice) : {};
                              // Format point with correct sign: favorite gets -, underdog gets +
                              const pointDisplay = point !== null && point !== undefined
                                ? (isFavorite ? `-${point}` : `+${point}`)
                                : null;

                              return (
                                <td key={`spreads-${book}-away`} style={{...oddsCellStyle, textAlign: 'center', padding: '4px', cursor: odds ? 'pointer' : undefined}}
                                  onClick={() => odds && onOddsClick && onOddsClick(safeGame.gameIDmlb, null, safeGame.away, 'spreads', book, odds, safeGame.away, safeGame.home, safeGame.away, null, pointDisplay || null, 'away', safeGame.awayTeamId)}
                                >
                                  {odds ? (
                                    <div style={{ ...cellStyle, padding: '4px', borderRadius: '3px' }}>
                                      <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{odds > 0 ? `+${odds}` : odds}</div>
                                      {pointDisplay && <div style={{ fontSize: '0.75rem', color: cellStyle.color || '#6c757d' }}>{pointDisplay}</div>}
                                    </div>
                                  ) : null}
                                </td>
                              );
                            });
                          })()}
                        </tr>

                        {/* Home Team Row */}
                        <tr>
                          <td
                            style={{ width: '120px', fontWeight: 'bold', cursor: 'pointer' }}
                            onClick={() => setShowGameModal(true)}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                              <img
                                src={safeGame.homeLogo || getTeamLogoUrl(safeGame.home)}
                                alt={safeGame.home}
                                style={{ width: '16px', height: '16px' }}
                                onError={(e) => {
                                  e.target.style.visibility = 'hidden';
                                  e.target.style.width = '0px';
                                  e.target.style.height = '0px';
                                  e.target.style.margin = '0px';
                                }}
                              />
                              <span>{safeGame.home}</span>
                            </div>
                          </td>
                          {(() => {
                            // Count how many books have the best price for spreads home
                            const allOdds = bookLogos.map(b => {
                              const rec = findMainLineForBook(spreadsArray, 'spreads', b, 'home');
                              return getSpreadsValue(rec, b, 'home');
                            }).filter(o => o !== undefined && o !== null);
                            const bestPrice = allOdds.length > 0 ? Math.max(...allOdds) : null;
                            const booksWithBestPrice = allOdds.filter(o => o === bestPrice).length;

                            return bookLogos.map(book => {
                              const bookRecord = findMainLineForBook(spreadsArray, 'spreads', book, 'home');
                              const spreadInfo = getSpreadsValueWithSign(bookRecord, book, 'home');
                              const odds = spreadInfo?.odds;
                              const isFavorite = spreadInfo?.isFavorite;
                              const point = bookRecord?.point;
                              const bestBookPrice = bookRecord?.['spreads_bestBookPrice_minus_home'];
                              const bestBookDiff = bookRecord?.['spreads_bestBookDiff_minus_home'];
                              const cellStyle = odds ? getCellStyle(odds, bestBookPrice, bestBookDiff, booksWithBestPrice) : {};
                              // Format point with correct sign: favorite gets -, underdog gets +
                              const pointDisplay = point !== null && point !== undefined
                                ? (isFavorite ? `-${point}` : `+${point}`)
                                : null;

                              return (
                                <td key={`spreads-${book}-home`} style={{...oddsCellStyle, textAlign: 'center', padding: '4px', cursor: odds ? 'pointer' : undefined}}
                                  onClick={() => odds && onOddsClick && onOddsClick(safeGame.gameIDmlb, null, safeGame.home, 'spreads', book, odds, safeGame.away, safeGame.home, safeGame.home, null, pointDisplay || null, 'home', safeGame.homeTeamId)}
                                >
                                  {odds ? (
                                    <div style={{ ...cellStyle, padding: '4px', borderRadius: '3px' }}>
                                      <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{odds > 0 ? `+${odds}` : odds}</div>
                                      {pointDisplay && <div style={{ fontSize: '0.75rem', color: cellStyle.color || '#6c757d' }}>{pointDisplay}</div>}
                                    </div>
                                  ) : null}
                                </td>
                              );
                            });
                          })()}
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>
            );
          })()}

          {/* View All Lines Button */}
          <div className="text-center mt-3">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => onGameClick ? onGameClick(safeGame.gameIDmlb) : setShowGameModal(true)}
              style={{ fontSize: '0.8rem' }}
            >
              View All Lines
            </button>
          </div>
        </div>
      )}

      {/* Game Modal for viewing all lines (fallback when onGameClick is not provided) */}
      {!onGameClick && (
        <H2HGameModal
          show={showGameModal}
          onHide={() => setShowGameModal(false)}
          game={safeGame}
          gameId={safeGame.gameIDmlb}
          sport={sport}
          getTeamLogoUrl={getTeamLogoUrl}
          getBookLogoUrl={getBookLogoUrl}
          bookmakers={bookmakers}
        />
      )}
    </div>
  );
};

// =============================================================================
// PERFORMANCE OPTIMIZATION: React.memo prevents re-renders when props unchanged
// Custom comparator checks only props that affect rendering
// To revert: change to `export default H2HDataTable;`
// =============================================================================
export default React.memo(H2HDataTable, (prevProps, nextProps) => {
  // Return true if props are equal (skip re-render)
  // Return false if props changed (trigger re-render)

  // Always re-render if data changed
  if (prevProps.data !== nextProps.data) return false;

  // Re-render if collapse state changed
  if (prevProps.isCollapsed !== nextProps.isCollapsed) return false;

  // Re-render if game status changed (for live games)
  if (prevProps.gameStatus !== nextProps.gameStatus) return false;

  // Re-render if loading state changed
  if (prevProps.isLoading !== nextProps.isLoading) return false;

  // Re-render if bookmakers list changed
  if (prevProps.bookmakers !== nextProps.bookmakers) return false;

  // Re-render if mobile state changed
  if (prevProps.isMobile !== nextProps.isMobile) return false;

  // Re-render if clicked cell changed (for animations)
  if (prevProps.clickedCellId !== nextProps.clickedCellId) return false;

  // Re-render if game object changed (for scores, logos, etc.)
  if (prevProps.game !== nextProps.game) return false;

  // Props are equal, skip re-render
  return true;
});
